export enum NotificationStatus {
  PENDING   = 'pending',
  SENT      = 'sent',
  OPENED    = 'opened',
  CONFIRMED = 'confirmed',
  DECLINED  = 'declined',
  EXPIRED   = 'expired',
  FAILED    = 'failed',
}

export interface FlightChangeData {
  flight_id?:          number;
  flight_number?:      string;
  airline_name?:       string;
  airline_logo?:       string;
  departure_date?:     string;
  departure_iata?:     string;
  departure_terminal?: string;
  arrival_date?:       string;
  arrival_iata?:       string;
  arrival_terminal?:   string;
  duration?:           string;
  stops?:              number;
}

export interface DiffField {
  labelHe: string;
  labelEn: string;
  old:     string;
  new:     string;
  changed: boolean;
}

export interface ChangeNotificationPublic {
  id:               number;
  uuid:             string;
  order_id:         number;
  change_type:      string;
  old_data:         FlightChangeData | null;
  new_data:         FlightChangeData | null;
  status:           NotificationStatus;
  customer_name:    string | null;
  customer_email:   string | null;
  expires_at:       string | null;
  confirmed_at:     string | null;
  declined_at:      string | null;
  diff_fields?:     DiffField[];
}

import moment from 'moment';

type FieldFormat = 'text' | 'date' | 'stops';

interface FieldConfig {
  key:     keyof FlightChangeData;
  labelHe: string;
  labelEn: string;
  format?: FieldFormat;
}

const FIELD_ORDER: FieldConfig[] = [
  { key: 'airline_name',       labelHe: 'חברת תעופה',       labelEn: 'Airline' },
  { key: 'flight_number',      labelHe: 'מספר טיסה',       labelEn: 'Flight No.' },
  { key: 'departure_date',     labelHe: 'שעת יציאה',        labelEn: 'Departure',     format: 'date' },
  { key: 'departure_iata',     labelHe: 'שדה תעופה יציאה',  labelEn: 'From' },
  { key: 'departure_terminal', labelHe: 'טרמינל יציאה',     labelEn: 'Dep. Terminal' },
  { key: 'arrival_date',       labelHe: 'שעת נחיתה',        labelEn: 'Arrival',       format: 'date' },
  { key: 'arrival_iata',       labelHe: 'שדה תעופה נחיתה',  labelEn: 'To' },
  { key: 'arrival_terminal',   labelHe: 'טרמינל נחיתה',     labelEn: 'Arr. Terminal' },
  { key: 'duration',           labelHe: 'משך טיסה',         labelEn: 'Duration' },
  { key: 'stops',              labelHe: 'עצירות',           labelEn: 'Stops',         format: 'stops' },
];

function formatValue(value: any, format?: FieldFormat): string {
  if (value === undefined || value === null || value === '') return '—';
  if (format === 'date') {
    // Display the time exactly as it comes from the API (UTC), without
    // shifting it to the viewer's local timezone.
    const m = typeof value === 'number' ? moment.utc(value * 1000) : moment.utc(value);
    if (!m.isValid()) return String(value);
    return m.format('DD/MM/YYYY HH:mm');
  }
  if (format === 'stops') return Number(value) === 0 ? 'ישיר / Direct' : String(value);
  return String(value);
}

export function buildFlightChangeDiff(
  oldData: FlightChangeData | null,
  newData: FlightChangeData | null,
): DiffField[] {
  const old = oldData || {};
  const nw  = newData || {};
  return FIELD_ORDER
    .filter(f => old[f.key] !== undefined || nw[f.key] !== undefined)
    .map(f => {
      const oldVal = formatValue(old[f.key], f.format);
      const newVal = formatValue(nw[f.key], f.format);
      return { labelHe: f.labelHe, labelEn: f.labelEn, old: oldVal, new: newVal, changed: oldVal !== newVal };
    });
}
